import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isValidHttpUrl } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import { DEVICE_TAB_TYPES } from '@/shared/config';
import { checkStatusStripChange, SAVED_SRIPS_IMAGES } from '@/widgets/strips-management/ui/handler';
import {
  selectChangeStripData,
  selectDeviceEventData,
  selectEventStripsData,
  selectIsLoadingStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { DeviceStripImageViewerProps } from '.';

/**
 * Backs the strip image viewer beside the device event list, including its edit modal. Reads the selected strip, event and device strip data and pending strip changes from the report store keyed by the tab, resolves the strip's SVG sources into gallery items through the image cache, tracks loading against the image component's loaded set, and records edits as strip changes in the store.
 *
 * @param props - Viewer props carrying the active device tab button and the report store key.
 * @returns An object carrying the viewer state, gallery items, image ref, loading and device-tab flags and the edit modal handlers.
 */
const useDeviceStripImageViewer = ({
  activeButton,
  keyRecoil,
}: Pick<DeviceStripImageViewerProps, 'activeButton' | 'keyRecoil'>) => {
  const savedImageRef = useRef<any>({});
  const imageRef = useRef<any>();

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const deviceEventsData = useReportStore(selectDeviceEventData(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip changes for this tab into the report store. Forwards the value to the store action under the tab's keyRecoil key.
   *
   * @param v - Next strip change list or updater accepted by the store action.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);

  const [state, setState] = useMergeState<{
    isLoading: boolean;
    isLoadedImageError: boolean;
    stripImgs?: any[];
    isVisibleEditAiStripModal?: boolean;
    selectedData?: any;
    originalSelectedData?: any;
  }>({
    isLoading: true,
    isLoadedImageError: false,
  });

  const [listImages, setListImages] = useState<any[]>([]);

  const isDeviceEvent = useMemo(() => DEVICE_TAB_TYPES.includes(activeButton), [activeButton]);

  const viewSelectedStrip = useMemo(() => {
    const changedStrip = _.find(changeStripData, (x) => x.id === selectedStrip?.id);
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark; originalInfo
    // is only on the local strip shapes built in StripsManagementV2/handler.ts - pre-existing gap.
    const { stripImgs } = changedStrip || (selectedStrip as any)?.originalInfo || {};
    if (stripImgs) {
      setState({ stripImgs });
    } else {
      setState({ stripImgs: [] });
    }
    return changedStrip || selectedStrip;
  }, [selectedStrip, changeStripData]);

  // const findCachedImage = (stripImg) => {
  //   const cachedImg = _.find(SAVED_SRIPS_IMAGES, x => x.baseUrl === stripImg);
  //   return cachedImg?.objectUrl ? `data:image/svg+xml;base64,${btoa(cachedImg?.objectUrl)}` : stripImg;
  // };

  /**
   * Resolves a strip image URL to a locally cached SVG when one has been downloaded. Looks the base URL up in the saved strip image cache and, when a cached body exists, wraps it in an SVG blob object URL, otherwise hands back the original URL.
   *
   * @param stripImg - Base URL of the strip image to resolve.
   * @returns A blob object URL for the cached SVG, or the original URL when nothing is cached.
   */
  const findCachedImage = (stripImg: any) => {
    const cachedImg = _.find(SAVED_SRIPS_IMAGES, (x) => x.baseUrl === stripImg);
    if (cachedImg?.objectUrl) {
      const svgBlob = new Blob([cachedImg?.objectUrl], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      return svgUrl;
    }
    return stripImg;
  };

  /**
   * Turns the selected strip's SVG sources into gallery items for the image viewer. Drops the combined all.svg entry, converts each remaining source into an SVG blob object URL or, for HTTP URLs, into its cached image, and labels each with its position in the list.
   *
   * @param stripImgs - Strip image sources, either HTTP URLs or inline SVG markup.
   * @returns Gallery items each carrying the original and thumbnail URLs and a position label.
   */
  const createItems = (stripImgs: any[]) => {
    const notIncludedAllStripImgs = _.filter(stripImgs, (x) => !_.includes(x, 'all.svg'));
    return _.map(notIncludedAllStripImgs, (stripImg, index) => {
      // const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : `data:image/svg+xml;base64,${btoa(stripImg)}`;
      const svgBlob = new Blob([stripImg], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : svgUrl;
      const description = `${index + 1}/${notIncludedAllStripImgs.length}`;
      return {
        original: formatImg,
        originalAlt: `ECG strip ${description}`,
        thumbnail: formatImg,
        thumbnailAlt: `ECG strip ${description} thumbnail`,
        description,
      };
    });
  };

  /**
   * Closes the edit strip modal and drops its working copies. Clears isVisibleEditAiStripModal along with the selected and original strip data in local state.
   *
   * @returns Nothing.
   */
  const closeEditStripModal = () => {
    setState({
      isVisibleEditAiStripModal: false,
      selectedData: {},
      originalSelectedData: {},
    });
  };

  /**
   * Opens the edit modal for the currently selected ECG strip. Looks the untouched strip up in the device event or event strip data for the active tab, prefers any pending change to the selected strip as the editable copy, and stores both in local state before showing the modal.
   *
   * @returns Nothing.
   */
  const openEditStripModal = () => {
    const originalSelectedData = selectedStrip
      ? _.find(
          isDeviceEvent ? (deviceEventsData as any)?.[activeButton] : eventStripsData,
          (x) => x.id === (selectedStrip as any).id,
        )
      : undefined;
    const changedStrip = _.find(changeStripData, (x) => x.id === selectedStrip?.id);
    setState({
      isVisibleEditAiStripModal: true,
      selectedData: changedStrip || selectedStrip,
      originalSelectedData,
    });
  };

  /**
   * Handles the edit button on the strip image viewer. Delegates to openEditStripModal, which resolves the original and pending copies of the selected strip and shows the modal.
   *
   * @returns Nothing.
   */
  const onClickEditStrip = () => {
    openEditStripModal();
  };

  /**
   * Commits edits made in the strip modal to the tab's pending strip changes. Closes the modal untouched when nothing was edited, otherwise clones the viewed strip, applies the update, reconciles it against the existing changes through checkStatusStripChange, stores the result and closes the modal.
   *
   * @param updateData - Fields edited in the strip modal, empty when the user made no change.
   * @returns Nothing.
   */
  const updateEditData = (updateData: any) => {
    if (_.isEmpty(updateData)) {
      closeEditStripModal();
      return;
    }
    const newUpdate = _.cloneDeep(viewSelectedStrip);
    _.assign(newUpdate, updateData);
    const changes = checkStatusStripChange([newUpdate], changeStripData);
    setChangeStripData(changes);
    closeEditStripModal();
  };

  /**
   * Decides whether the strip image gallery should still show its loading state. Turns loading on while strip images are pending, clears it once every URL appears in the image component's loaded set or any entry is not a valid HTTP URL, and otherwise clears it as soon as a URL is seen a second time in the saved image ref.
   *
   * @returns Nothing.
   */
  const checkLoading = () => {
    if (!_.isEmpty(state.stripImgs)) {
      setState({ isLoading: true });
      if (!_.isEmpty(imageRef.current?.loadedImages)) {
        const loadedImgArr = Object.keys(imageRef.current?.loadedImages);
        if (_.every(state.stripImgs, (item) => loadedImgArr.includes(item))) {
          setState({ isLoading: false });
          return;
        }
      }

      if (_.some(state.stripImgs, (x) => x && !isValidHttpUrl(x))) {
        setState({ isLoading: false });
        return;
      }
      _.forEach(state.stripImgs, (item) => {
        if (!savedImageRef.current[item]) {
          savedImageRef.current[item] = item;
        } else {
          setState({ isLoading: false });
        }
      });
    }
    _.forEach(state.stripImgs, (item) => {
      if (!savedImageRef.current[item]) {
        savedImageRef.current[item] = item;
      }
    });
  };

  useEffect(() => {
    setState({ isLoadedImageError: false });
    checkLoading();
    if (state.stripImgs?.length) {
      const stripImgs = createItems(state.stripImgs);
      setListImages(stripImgs);
    }
  }, [JSON.stringify(state.stripImgs)]);

  return {
    state,
    setState,
    listImages,
    imageRef,
    isLoadingStrip,
    viewSelectedStrip,
    isDeviceEvent,
    onClickEditStrip,
    closeEditStripModal,
    updateEditData,
  };
};

export default useDeviceStripImageViewer;
